/* eslint-disable no-extra-parens */
/* eslint-disable react/display-name */
import React, { memo, useEffect, useRef, useCallback } from 'react';
import styled from 'styled-components';
import { reduxConnect } from '../../../../common';
import { setForecastScrollButtonStatus } from '../../../../common/actions/forecastPageScrollActions';
import { mediumMediaQuery, smallScreenMaxWidth } from '../../../../common/utilities/sizeHelpers';
import { ForecastsCardTrayNavItem } from './forecastsCardTrayNavItem';
import { ForecastPageIds, ScrollButtonStatus, ScrollDirections } from '../../common/types';
import { dailyForecastScrollableViewId, hourlyForecastScrollableViewId, forecastViewScrollHandler } from '../../common/utils';
import { IStoreState } from '../../../common/interfaces/types';
import { HourlyForecastPageStateType } from '../../../../common/reducers/hourlyForecastPageViewReducer';
import { ForecastScrollStateType } from '../../../../common/reducers/forecastPageScrollReducer';

interface ForecastsCardTrayNavProps {
    scrollButtonStatus: ScrollButtonStatus;
    hasExpandedAllCards: boolean;
    scrollableViewId: string;
    routeString: string;
    setForecastScrollButtonStatus(buttonStatus: ScrollButtonStatus): void;
}

interface ForecastStateState extends IStoreState {
    hourlyForecastPage: HourlyForecastPageStateType;
    forecastPageScroll: ForecastScrollStateType;
}

const mapStoreStateToProps = (storeState: ForecastStateState) => {
    const { hourlyForecastPage, appState, forecastPageScroll } = storeState || {};
    const { pageId, user } = appState?.appState || {};
    const { ActiveLocation } = user || {};
    const { Locality } = ActiveLocation || {};
    const { RouteString } = Locality || {};
    const { scrollButtonStatus } = forecastPageScroll || {};
    const { hourlyForecastPeriods, apiHourlyForecastPeriods } = hourlyForecastPage || {};

    let scrollableViewId;
    if (pageId?.toLowerCase() === ForecastPageIds.hourly) {
        scrollableViewId = hourlyForecastScrollableViewId;
    } else if (pageId?.toLowerCase() === ForecastPageIds.daily) {
        scrollableViewId = dailyForecastScrollableViewId;
    }
    const hasExpandedAllHourlyCards = pageId?.toLowerCase() === ForecastPageIds.hourly && hourlyForecastPeriods?.length && hourlyForecastPeriods?.length === apiHourlyForecastPeriods?.length;

    return {
        scrollableViewId,
        scrollButtonStatus,
        hasExpandedAllCards: hasExpandedAllHourlyCards ? hasExpandedAllHourlyCards : false,
        routeString: RouteString
    };
};

const mapDispatchToProps = {
    setForecastScrollButtonStatus
};

export const ForecastsCardTrayNav = reduxConnect(mapStoreStateToProps, mapDispatchToProps)(memo(({
    hasExpandedAllCards,
    scrollableViewId,
    setForecastScrollButtonStatus,
    routeString,
    scrollButtonStatus }: ForecastsCardTrayNavProps) => {

    const isInitialButtonStatusCheckRef = useRef<boolean>(true);
    const prevLocationRouteStringRef = useRef<string>('');

    useEffect(() => {
        // reset on unmount
        return () => {
            const defaultButtonStatus: ScrollButtonStatus = {
                isRightScrollButtonDisabled: false,
                isLeftScrollButtonDisabled: true
            };
            setForecastScrollButtonStatus(defaultButtonStatus);
        };
    }, []);
    
    useEffect(() => {

        if ((isInitialButtonStatusCheckRef.current && scrollableViewId) 
            || (prevLocationRouteStringRef.current && routeString && prevLocationRouteStringRef.current !== routeString)) { // should happen only once on page load/refresh/active location update
            isInitialButtonStatusCheckRef.current = false;
            prevLocationRouteStringRef.current = routeString ? routeString : '';
            const { isLeftScrollButtonDisabled, isRightScrollButtonDisabled } = scrollButtonStatus;
             
            const forecastScrollableView = document.getElementById(scrollableViewId);

            if (forecastScrollableView) {
                const newButtonStatus: ScrollButtonStatus = {
                    isRightScrollButtonDisabled: false,
                    isLeftScrollButtonDisabled: false
                };
                const { scrollLeft, scrollWidth, offsetWidth } = forecastScrollableView;
                const rightScrollEnd = scrollLeft + offsetWidth - scrollWidth;
                if (scrollLeft <= 3) {
                    newButtonStatus.isLeftScrollButtonDisabled = true;
                }
                if (rightScrollEnd === 0 && scrollLeft !== 0 && offsetWidth > smallScreenMaxWidth && !isRightScrollButtonDisabled) {
                    newButtonStatus.isRightScrollButtonDisabled = true;
                }
                setForecastScrollButtonStatus(newButtonStatus);
            }
        }
    }, [scrollButtonStatus.isLeftScrollButtonDisabled, scrollButtonStatus.isLeftScrollButtonDisabled, hasExpandedAllCards, scrollableViewId, routeString]);

    const handleClick = useCallback((scrollDirection: ScrollDirections) => {
        const forecastScrollableView = document.getElementById(scrollableViewId);
        let scrollOffset = 0;
        
        if (forecastScrollableView) {
            const baseScrollWidth = forecastScrollableView?.offsetWidth;
            if (scrollDirection === ScrollDirections.left) {
                scrollOffset = forecastScrollableView?.scrollLeft - forecastScrollableView?.offsetWidth;
            } else {
                scrollOffset = baseScrollWidth + forecastScrollableView?.scrollLeft;
            }
            forecastViewScrollHandler(forecastScrollableView, 500, scrollOffset);
        }
    }, [scrollableViewId]);

    return (
        <Container isDailyView={scrollableViewId === dailyForecastScrollableViewId}>
            <ForecastsCardTrayNavItem
                key={`scroll-btn-${ScrollDirections.left}`}
                handleClick={() => handleClick(ScrollDirections.left)} 
                isDisabled={scrollButtonStatus.isLeftScrollButtonDisabled}
                arrowDirection={-180} />
            <ForecastsCardTrayNavItem 
                key={`scroll-btn-${ScrollDirections.right}`}
                handleClick={() => handleClick(ScrollDirections.right)} 
                isDisabled={scrollButtonStatus.isRightScrollButtonDisabled}
                arrowDirection={0} />
        </Container>
    );
}));

interface ContainerProps {
    isDailyView: boolean;
}

const Container = styled.div<ContainerProps>`
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    z-index: 10;
    margin-top: ${({ isDailyView }) => isDailyView ? '255.5px' : '269px'};
    height: 34px;
    width: 100%;
    
    @media ${mediumMediaQuery} {
        justify-content: center;
        padding-left: 0%;
        margin-top: 0;
    }
`;